import {
  getProductByIds,
  getProductsByLogic,
} from "@/requests/product";
import { shuffleArray } from "@/utils";
import { useEffect, useRef, useState } from "react";
import { useOnScreen } from "./useOnScreen";

/**
 * All three hooks now accept `initialProducts` — product data prefetched on
 * the server by PageWrapper (see lib/widget-products.ts). When provided and
 * the query hasn't changed, the client fetch is skipped entirely: the
 * products are already in the server-rendered HTML.
 */

const queryKey = (custom?: { id: number }[], logical?: LogicBuilder) =>
  JSON.stringify({
    c: custom?.map((p) => p.id) || null,
    l: logical || null,
  });

const useLogicalProducts = ({
  widget,
  initialProducts,
}: {
  widget?: Widget;
  initialProducts?: ProductItem[];
}) => {
  const hasInitial = !!initialProducts?.length;

  const [products, setProducts] = useState<ProductItem[]>(
    initialProducts || [],
  );
  const [loading, setLoading] = useState<boolean>(!hasInitial);
  const [error, setError] = useState<any>();

  const custom = widget?.data?.setting?.custom;
  const logical = widget?.data?.setting?.logical;
  const key = queryKey(custom, logical);
  const initialKey = useRef<string | null>(hasInitial ? key : null);

  useEffect(() => {
    // server already provided this exact query's products
    if (initialKey.current && key === initialKey.current) {
      setProducts(initialProducts || []);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(undefined);

    if (!!custom?.length) {
      getProductByIds(custom.map((p) => p.id) || [])
        .then((res) => {
          setProducts(res.data);
          setLoading(false);
        })
        .catch((err) => {
          setError(err);
          setLoading(false);
        });
    } else if (!!logical) {
      getProductsByLogic(logical)
        .then((res) => {
          if (
            !logical?.isFeatured &&
            (logical?.categoryOrder === "random" ||
              logical?.brandOrder === "random")
          ) {
            shuffleArray(res);
          }
          setProducts(res);
          setLoading(false);
        })
        .catch((err) => {
          setError(err);
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return {
    products,
    loading,
    error,
  };
};

const useLogicalProductsManual = ({
  custom = [],
  logical,
  ref,
  apiCallback,
  initialProducts,
}: {
  custom?: { id: number }[];
  logical?: LogicBuilder;
  ref: React.RefObject<HTMLDivElement | null>;
  apiCallback?: (products?: ProductItem[]) => void;
  initialProducts?: ProductItem[];
}) => {
  const isIntersecting = useOnScreen(ref, false);

  const hasInitial = !!initialProducts?.length;

  const [products, setProducts] = useState<ProductItem[]>(
    initialProducts || [],
  );
  const [loading, setLoading] = useState<boolean>(!hasInitial);
  const [error, setError] = useState<any>();

  const key = queryKey(custom, logical);
  const initialKey = useRef<string | null>(hasInitial ? key : null);
  const initialReported = useRef(false);

  useEffect(() => {
    // server already provided this exact query's products (e.g. the first
    // filter tab) — reuse it, including when the user switches back to it
    if (initialKey.current && key === initialKey.current) {
      setProducts(initialProducts || []);
      setLoading(false);
      if (!initialReported.current) {
        initialReported.current = true;
        apiCallback?.(initialProducts);
      }
      return;
    }

    if (isIntersecting) {
      if (!!custom.length) {
        setLoading(true);

        getProductByIds(custom?.map((p: any) => p?.id ?? p) || [])
          .then((res) => {
            setProducts(res.data);
            setLoading(false);
            apiCallback?.(res.data);
          })
          .catch((err) => {
            setError(err);
            setLoading(false);
            apiCallback?.();
          });
      } else if (!!logical) {
        setLoading(true);

        getProductsByLogic(logical)
          .then((res) => {
            if (
              !logical?.isFeatured &&
              (logical?.categoryOrder === "random" ||
                logical?.brandOrder === "random")
            ) {
              shuffleArray(res);
            }

            setProducts(res);
            setLoading(false);
            apiCallback?.(res);
          })
          .catch((err) => {
            setError(err);
            setLoading(false);
            apiCallback?.();
          });
      } else {
        setLoading(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, isIntersecting]);

  return {
    products,
    loading,
    error,
  };
};

const useLogicalProductsWithInterceptors = ({
  widget,
  ref,
  initialProducts,
}: {
  widget?: Widget;
  ref: React.RefObject<HTMLDivElement | null>;
  initialProducts?: ProductItem[];
}) => {
  const isIntersecting = useOnScreen(ref, false);

  const hasInitial = !!initialProducts?.length;

  const [products, setProducts] = useState<ProductItem[]>(
    initialProducts || [],
  );
  const [loading, setLoading] = useState<boolean>(!hasInitial);
  const [error, setError] = useState<any>();

  const custom = widget?.data?.setting?.custom;
  const logical = widget?.data?.setting?.logical;
  const key = queryKey(custom as any, logical);
  const initialKey = useRef<string | null>(hasInitial ? key : null);

  useEffect(() => {
    // server already provided this exact query's products
    if (initialKey.current && key === initialKey.current) {
      setProducts(initialProducts || []);
      setLoading(false);
      return;
    }

    if (isIntersecting) {
      if (!!custom?.length) {
        getProductByIds(custom.map((p: any) => p.id) || [])
          .then((res) => {
            setProducts(res.data);
            setLoading(false);
          })
          .catch((err) => {
            setError(err);
            setLoading(false);
          });
      } else if (!!logical) {
        getProductsByLogic(logical)
          .then((res) => {
            if (
              !logical?.isFeatured &&
              (logical?.categoryOrder === "random" ||
                logical?.brandOrder === "random")
            ) {
              shuffleArray(res);
            }
            setProducts(res);
            setLoading(false);
          })
          .catch((err) => {
            setError(err);
            setLoading(false);
          });
      } else {
        setLoading(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, isIntersecting]);

  return {
    products,
    loading,
    error,
  };
};

// ** exports
export default useLogicalProducts;
export { useLogicalProductsManual, useLogicalProductsWithInterceptors };
